'use client'

import { useEffect, useState, useMemo, useCallback, useRef, type ReactNode } from 'react'
import { Card, BlockStack, Text, InlineStack, Button, Spinner, Box } from '@shopify/polaris'
import { useAuthenticatedFetch } from '@/app/(app)/hooks/useAuthenticatedFetch'
import { SigmaMiniLineChart, type LineSeriesPoint } from '@/components/charts/SigmaMiniLineChart'
import { useSigmaSurface } from '@/components/SigmaSurfaceProvider'

type SeriesRow = { date: string; deliveredRevenue: number; totalProfit: number; orders: number }

type MetricKey = 'deliveredRevenue' | 'totalProfit' | 'orders'

type Props = {
  shop: string | null
  host: string | null
  dateFrom: string
  dateTo: string
  drilldownLinks?: ReactNode
  /** Portal: do not fetch series until the card is near the viewport (saves one API on first paint). */
  deferUntilVisible?: boolean
}

export function DashboardTrendCard({
  shop,
  host,
  dateFrom,
  dateTo,
  drilldownLinks,
  deferUntilVisible = false,
}: Props) {
  const authenticatedFetch = useAuthenticatedFetch()
  const surface = useSigmaSurface()
  const isPortal = surface === 'portal'
  const toggleVariant = (active: boolean) => (active ? 'primary' : isPortal ? 'secondary' : 'tertiary')
  const [metric, setMetric] = useState<MetricKey>('deliveredRevenue')
  const [series, setSeries] = useState<SeriesRow[] | null>(null)
  const [loading, setLoading] = useState(() => !deferUntilVisible)
  const [error, setError] = useState<string | null>(null)
  const [inView, setInView] = useState(!deferUntilVisible)
  const visibilityRef = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    if (!shop) {
      setSeries([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const apiBase = typeof window !== 'undefined' ? window.location.origin : ''
      const params = new URLSearchParams({
        shop,
        createdAtMin: dateFrom,
        createdAtMax: dateTo,
      })
      if (host) params.set('host', host)
      const res = await authenticatedFetch(`${apiBase}/api/analytics/dashboard-series?${params}`)
      const json = (await res.json()) as { series?: SeriesRow[]; error?: string }
      if (!res.ok) {
        setError(typeof json.error === 'string' ? json.error : 'Could not load trend')
        setSeries([])
        return
      }
      setSeries(Array.isArray(json.series) ? json.series : [])
    } catch {
      setError('Could not load trend')
      setSeries([])
    } finally {
      setLoading(false)
    }
  }, [authenticatedFetch, shop, host, dateFrom, dateTo])

  useEffect(() => {
    if (!deferUntilVisible) {
      setInView(true)
      return
    }
    const el = visibilityRef.current
    if (!el || typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }
    let cancelled = false
    const io = new IntersectionObserver(
      (entries) => {
        if (cancelled || !entries.some((e) => e.isIntersecting)) return
        setInView(true)
        io.disconnect()
      },
      { root: null, rootMargin: '160px', threshold: 0 }
    )
    io.observe(el)
    return () => {
      cancelled = true
      io.disconnect()
    }
  }, [deferUntilVisible, dateFrom, dateTo, shop])

  useEffect(() => {
    if (!inView) return
    let idleId: number | undefined
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    const start = () => {
      void load()
    }
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(start, { timeout: 2800 })
    } else {
      timeoutId = setTimeout(start, 1)
    }
    return () => {
      if (idleId !== undefined && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId)
      }
      if (timeoutId !== undefined) clearTimeout(timeoutId)
    }
  }, [load, inView])

  const points: LineSeriesPoint[] = useMemo(() => {
    if (!series?.length) return []
    return series.map((r, i) => ({
      x: i,
      y:
        metric === 'deliveredRevenue'
          ? r.deliveredRevenue
          : metric === 'totalProfit'
            ? r.totalProfit
            : r.orders,
      label: r.date,
    }))
  }, [series, metric])

  const stroke =
    metric === 'totalProfit'
      ? 'var(--sigma-semantic-profit)'
      : 'var(--sigma-semantic-info)'

  return (
    <Card>
      <div ref={visibilityRef}>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center" wrap gap="200">
          <Text variant="headingMd" as="h2">
            Daily trend
          </Text>
          <InlineStack gap="100" wrap>
            <Button
              size="slim"
              variant={toggleVariant(metric === 'deliveredRevenue')}
              onClick={() => setMetric('deliveredRevenue')}
            >
              Delivered revenue
            </Button>
            <Button size="slim" variant={toggleVariant(metric === 'totalProfit')} onClick={() => setMetric('totalProfit')}>
              Profit
            </Button>
            <Button size="slim" variant={toggleVariant(metric === 'orders')} onClick={() => setMetric('orders')}>
              Orders
            </Button>
          </InlineStack>
        </InlineStack>
        <div className={`sigma-dashboard-chart-well${isPortal ? ' sigma-dashboard-chart-well--portal' : ''}`}>
          <Box
            padding="300"
            background={isPortal ? 'bg-surface' : 'bg-surface-secondary'}
            borderRadius="200"
          >
            {deferUntilVisible && !inView ? (
              <div style={{ minHeight: 100 }} />
            ) : loading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 140,
                }}
              >
                <Spinner size="small" accessibilityLabel="Loading trend" />
              </div>
            ) : error ? (
              <Box paddingBlockStart="200" paddingBlockEnd="200" paddingInline="100">
                <Text as="p" variant="bodySm" tone="subdued">
                  {error}
                </Text>
              </Box>
            ) : (
              <SigmaMiniLineChart
                points={points}
                stroke={stroke}
                height={140}
                strokeWidth={isPortal ? 2 : 1.25}
                areaFill={isPortal}
                areaOpacity={0.08}
                showGrid={isPortal}
                animate={isPortal}
              />
            )}
          </Box>
        </div>
        {drilldownLinks ? (
          <InlineStack gap="300" wrap>
            {drilldownLinks}
          </InlineStack>
        ) : null}
      </BlockStack>
      </div>
    </Card>
  )
}
